import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { img, PrimaryButton, cx } from './ui';
import { useInquiryCart } from '../../../context/InquiryCartContext';

const PROJECT_TYPES = [
  'Dealer / Retailer',
  'Interior Designer',
  'Hospitality / Project',
  'Residential',
  'Corporate / Office',
  'Other',
];
const CONTACT_METHODS = ['Email', 'Phone', 'WhatsApp'];

const EMPTY = {
  name: '',
  company: '',
  email: '',
  phone: '',
  city: '',
  country: '',
  project_type: PROJECT_TYPES[0],
  preferred_contact: 'Email',
  message: '',
};

export default function InquiryFormModal({ open, onClose }) {
  const { items, submitInquiry } = useInquiryCart();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm(EMPTY);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry(form);
      setDone(true);
    } catch {
      toast.error('Could not send your inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    'w-full px-4 py-3 border border-[#241811]/15 bg-[#faf5ec] text-sm outline-none focus:border-[#b5622f] transition-colors';
  const lab = 'text-[11px] uppercase tracking-[0.14em] text-[#4a3d32] block mb-1.5';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Send inquiry">
      <div className="absolute inset-0 bg-[#160f0a99] backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#f4ece0] shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-7 py-5 bg-[#241811] text-[#f4ece0] z-10">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#d98a52]">Quotation request</div>
            <h3 className="font-display text-xl mt-0.5">Send your inquiry</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1 hover:text-[#d98a52]">
            <X />
          </button>
        </div>

        {done ? (
          <div className="px-7 py-16 text-center">
            <div className="font-display text-2xl mb-3">Inquiry received</div>
            <p className="text-[#4a3d32] text-sm max-w-sm mx-auto">
              Thank you. Our team will review your selection and respond within one business day.
            </p>
            <PrimaryButton onClick={onClose} className="mt-8">Continue browsing</PrimaryButton>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6">
            {items.length > 0 && (
              <div className="mb-6 border border-[#241811]/12 bg-[#faf5ec]">
                <div className="px-4 py-2.5 text-[11px] uppercase tracking-[0.16em] text-[#4a3d32] border-b border-[#241811]/10">
                  {items.length} item{items.length !== 1 ? 's' : ''} in this inquiry
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-[#241811]/8">
                  {items.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 px-4 py-2.5">
                      <img src={img(it.image_url)} alt="" className="w-10 h-12 object-cover flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] truncate">{it.name}</div>
                        <div className="text-[11px] text-[#4a3d32]">Qty {it.qty}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={lab}>Full name *</label>
                <input className={field} value={form.name} onChange={set('name')} placeholder="Your name" />
              </div>
              <div>
                <label className={lab}>Company</label>
                <input className={field} value={form.company} onChange={set('company')} placeholder="Business name" />
              </div>
              <div>
                <label className={lab}>Email</label>
                <input type="email" className={field} value={form.email} onChange={set('email')} placeholder="you@company.com" />
              </div>
              <div>
                <label className={lab}>Phone *</label>
                <input className={field} value={form.phone} onChange={set('phone')} placeholder="+91 00000 00000" />
              </div>
              <div>
                <label className={lab}>City</label>
                <input className={field} value={form.city} onChange={set('city')} placeholder="City" />
              </div>
              <div>
                <label className={lab}>Country</label>
                <input className={field} value={form.country} onChange={set('country')} placeholder="Country" />
              </div>
              <div>
                <label className={lab}>Project type</label>
                <select className={cx(field, 'appearance-none')} value={form.project_type} onChange={set('project_type')}>
                  {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={lab}>Preferred contact</label>
                <select className={cx(field, 'appearance-none')} value={form.preferred_contact} onChange={set('preferred_contact')}>
                  {CONTACT_METHODS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className={lab}>Message</label>
              <textarea rows={3} className={field} value={form.message} onChange={set('message')} placeholder="Quantities, finishes, timelines, or anything else we should know." />
            </div>

            <div className="mt-6 flex items-center gap-4">
              <PrimaryButton onClick={submit} className="disabled:opacity-60">
                {submitting ? 'Sending…' : 'Send inquiry'}
              </PrimaryButton>
              <span className="text-[12px] text-[#4a3d32]">We usually respond within 24 hours.</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
